import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Fade, List } from '@mui/material';
import useGetNavigationItems, {
	MenuItemsIds,
} from '../../../../config/useGetNavigationItems';
import { ListConfigType } from '../../SideNav/SideNavContainer';
import { CustomListItemList } from '../../CustomListItemList/CustomListItemList';
import styles from './MobileMenu.module.scss';
import addClickHandler from './utils';
import { useDeviceDetection } from '../../../../hooks/useDeviceDetection';
import { useMobileMenuHelpItems } from './useMobileMenuHelpItems';
import { PremiumSubscriptionRequiredModal } from '../../../PremiumSubscriptionRequiredModal';
import useProfileLinks from '../../../../config/useProfileLinks';
import ClientOnboardingModal from '../../../ClientOnboardingModal';
import { OrgOnboardingModal } from '../../../OrgOnboardingModal';

export const MobileMenuContainer = ( {
	active,
	closeMobileMenu,
}: {
	active: boolean;
	closeMobileMenu: () => void;
} ) => {
	const {
		items: navigationItems,
		subscriptionNeededModalOpen: open,
		closeSubscriptionNeededModal: onClose,
	} = useGetNavigationItems();

	const profileItems = useProfileLinks();
	const {
		items: helpItems,
		clientOnboardingModalProps,
		orgOnboardingModalProps,
	} = useMobileMenuHelpItems();

	const config: ListConfigType = useMemo(
		() =>
			[
				navigationItems[ MenuItemsIds.Dashboard ],
				...profileItems,
				navigationItems[ MenuItemsIds.Leads ],
				navigationItems[ MenuItemsIds.Contacts ],
				navigationItems[ MenuItemsIds.Proposals ],
				navigationItems[ MenuItemsIds.Contracts ],
				navigationItems[ MenuItemsIds.Invoices ],
				navigationItems[ MenuItemsIds.Vendors ],
				navigationItems[ MenuItemsIds.ClientUsers ],
				navigationItems[ MenuItemsIds.OrgUsers ],
				navigationItems[ MenuItemsIds.Organizations ],
				navigationItems[ MenuItemsIds.Files ],
				navigationItems[ MenuItemsIds.Resources ],
				navigationItems[ MenuItemsIds.Reports ],
				navigationItems[ MenuItemsIds.Timelines ],
				...helpItems,
			].map( ( rawItem ) => {
				if ( 'icon' in rawItem ) {
					const { icon, ...item } = rawItem;
					return item;
				} else {
					return rawItem;
				}
			} ),
		[
			navigationItems,
			profileItems,
			helpItems
		]
	);

	const configWithClickHandler = useMemo(
		() => addClickHandler( config, closeMobileMenu ),
		[ config, closeMobileMenu ]
	);

	const [ interactive, setInteractive ] = useState( false );
	const isPointerInteractive = active && interactive;

	const { isDesktop } = useDeviceDetection();
	if ( isDesktop ) return null;

	return (
		<>
			<Fade
				in={ active }
				mountOnEnter
				unmountOnExit
				onEntering={ () => setInteractive( false ) }
				onEntered={ () => setInteractive( true ) }
				onExiting={ () => setInteractive( false ) }
			>
				<List
					className={ classNames( styles.root, {
						[ styles.nonInteractive ]: !isPointerInteractive,
					} ) }
				>
					<CustomListItemList items={ configWithClickHandler } />
				</List>
			</Fade>
			<PremiumSubscriptionRequiredModal open={ open } onClose={ onClose } />
			<ClientOnboardingModal { ...clientOnboardingModalProps } />
			<OrgOnboardingModal { ...orgOnboardingModalProps } />
		</>
	);
};
